import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FunctionComponent, useEffect, useState } from 'react';
import ToggleTabs from '../../components/ToogleTabs/ToggleTabs';
import MetricDelta from '../components/MetricDelta';
import { formatCurrency, getPaidChannelData, PaidChannel } from '../data/mockData';
import CampaignsTable, { CampaignSort } from './CampaignsTable';
import { DailyClientsBarChart } from './charts/DailyClientsBarChart';
import { DailySpendAreaChart } from './charts/DailySpendAreaChart';
import styles from './PaidChannels.module.scss';

interface PaidChannelsProps {
    description?: string;
}

export const PaidChannels: FunctionComponent<PaidChannelsProps> = props => {
    const [channel, setChannel] = useState<PaidChannel>('google');
    const [sort, setSort] = useState<CampaignSort>('spend');
    const [loading, setLoading] = useState(true);

    const data = getPaidChannelData(channel);

    useEffect(() => {
        setLoading(true);
        const timeout = setTimeout(() => setLoading(false), 600);
        return () => clearTimeout(timeout);
    }, [channel]);

    return (
        <div className={styles.container}>
            <span className={styles.description}>{props.description}</span>

            <div className={styles.toolbar}>
                <ToggleTabs
                    options={[
                        { id: 'google', label: 'Google Ads' },
                        { id: 'meta', label: 'Meta Ads' }
                    ]}
                    value={channel}
                    setValue={id => setChannel(id as PaidChannel)}
                />
            </div>

            <div className={styles.metrics}>
                <MetricDelta
                    title="Spend (Last 30d)"
                    value={formatCurrency(data.spend)}
                    delta={data.spendDelta}
                    isLoading={loading}
                />
                <MetricDelta
                    title="New Clients"
                    value={data.newClients.toString()}
                    delta={data.newClientsDelta}
                    isLoading={loading}
                />
                <MetricDelta
                    title="Cost per Client"
                    value={formatCurrency(data.cpa)}
                    invertColors
                    isLoading={loading}
                />
                <MetricDelta
                    title="ROAS (90-Day Revenue)"
                    value={`${data.roas.toFixed(1)}x`}
                    isLoading={loading}
                />
            </div>

            <div className={styles.chartsRow}>
                <div className={styles.chartCard}>
                    <h3 className={styles.sectionTitle}>Daily Spend · Last 30 Days</h3>
                    <DailySpendAreaChart
                        daily={data.daily}
                        loading={loading}
                    />
                </div>
                <div className={styles.chartCard}>
                    <h3 className={styles.sectionTitle}>Daily New Clients · Last 30 Days</h3>
                    <DailyClientsBarChart
                        daily={data.daily}
                        loading={loading}
                    />
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Campaigns</h3>
                    <ToggleTabs
                        options={[
                            { id: 'spend', label: 'By spend' },
                            { id: 'roas', label: 'By ROAS' }
                        ]}
                        value={sort}
                        setValue={id => setSort(id as CampaignSort)}
                    />
                </div>
                <CampaignsTable
                    campaigns={data.campaigns}
                    sort={sort}
                    loading={loading}
                />
            </div>

            <div className={styles.infoBanner}>
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>
                    Campaign performance is measured from tracked bookings and your connected ad accounts. Manage
                    connections in Business Settings → Integrations.
                </span>
            </div>
        </div>
    );
};

export default PaidChannels;
